import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ExportStudentsButton from "./ExportStudentsButton";
import {
  studentsService,
  TeacherStudentInfo,
} from "../../services/studentsService";

const StudentsListTable = () => {
  const { t, i18n } = useTranslation();
  const [students, setStudents] = useState<TeacherStudentInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setIsLoading(true);
        const data = await studentsService.getMyStudents();
        setStudents(data);
      } catch (err) {
        setError("Failed to fetch students");
        console.error("Error fetching students:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStudents();
  }, []);

  const toggleStudentAttestation = async (studentId: number, subjectId: number) => {
    // Create new students array with optimistic update
    const updatedStudents = students.map(student => {
      if (student.studentId === studentId) {
        return {
          ...student,
          attestationInfo: {
            ...student.attestationInfo,
            attestation: !student.attestationInfo?.attestation
          }
        };
      }
      return student;
    });
    setStudents(updatedStudents as TeacherStudentInfo[]);

    try {
      await studentsService.toggleStudentAttestation(studentId, subjectId);
    } catch (error) {
      // Revert on error
      console.error("Error toggling student attestation:", error);
      setStudents(students); // Restore original state
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2">{t("Loading students...")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark p-6">
        <div className="text-danger font-medium text-center">{error}</div>
      </div>
    );
  }

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div className="flex-auto p-6 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h4 className="text-xl font-semibold text-black dark:text-white">
            {t("Students List")}
          </h4>
          <ExportStudentsButton />
        </div>

        <div className="block w-full overflow-auto scrolling-touch">
          <table className="w-full max-w-full mb-4">
            <thead className="bg-gray-300 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-2 border dark:border-gray-200 text-left">
                  {t("Subject")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200 text-left">
                  {t("Speciality")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200 text-left">
                  {t("Semester")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200 text-left">
                  {t("Faculty Number")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200 text-left">
                  {t("Name")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200 text-left">
                  {t("Email")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200 text-left">
                  {t("Group")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200 text-left">
                  {t("Attestation Date")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200 text-left">
                  {t("Attestation Teacher")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200 text-left">
                  {t("Attestation")}
                </th>
              </tr>
            </thead>
            <tbody>
              {students
                .sort((a, b) => {
                  if (a.subjectName !== b.subjectName) return (a.subjectName || '').localeCompare(b.subjectName || '');
                  const specA = i18n.language === "bg" ? a.specialityName : a.specialityNameEn;
                  const specB = i18n.language === "bg" ? b.specialityName : b.specialityNameEn;
                  if (specA !== specB) return (specA || '').localeCompare(specB || '');
                  return a.semester - b.semester;
                })
                .map((student) => (
                  <tr
                    key={student.studentId}
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-2 border dark:border-gray-600">
                      {student.subjectName}
                    </td>
                    <td className="px-4 py-2 border dark:border-gray-600">
                      {i18n.language === "bg" ? student.specialityName : student.specialityNameEn}
                    </td>
                    <td className="px-4 py-2 border dark:border-gray-600">
                      {student.semester}
                    </td>
                    <td className="px-4 py-2 border dark:border-gray-600">
                      {student.facultyNumber}
                    </td>
                    <td className="px-4 py-2 border dark:border-gray-600">
                      {student.studentFirstName} {student.studentMiddleName} {student.studentLastName}
                    </td>
                    <td className="px-4 py-2 border dark:border-gray-600">
                      {student.studentEmail}
                    </td>
                    <td className="px-4 py-2 border dark:border-gray-600">
                      {student.studentGroupName || "-"}
                    </td>
                    <td className="px-4 py-2 border dark:border-gray-600">
                      {student.attestationInfo?.attestationDate || "-"}
                    </td>
                    <td className="px-4 py-2 border dark:border-gray-600">
                      {student.attestationInfo?.attestationTeacherName || "-"}
                    </td>
                    <td className="px-4 py-2 border dark:border-gray-600">
                      <button
                        className={`bg-blue-500 text-white font-bold py-2 px-4 rounded
                          ${student.attestationInfo?.attestation === true
                            ? "bg-green-500"
                            : student.attestationInfo?.attestation === false
                            ? "bg-red-500"
                            : ""
                          }`}
                        onClick={() =>
                          toggleStudentAttestation(
                            student.studentId,
                            student.subjectId
                          )
                        }
                      >
                        {student.attestationInfo?.attestation === true
                          ? t("Attested")
                          : student.attestationInfo?.attestation === false
                          ? t("Not Attested")
                          : t("Toggle Attestation")}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StudentsListTable;
